import {LoginSuccessAction, LogoutAction} from '../../../../src/app/shared/auth/auth.actions';
import {Store} from '@ngrx/store';
import {IState} from '../../../../src/app/shared/models/state';
import {UserAddAction} from '../../../../src/app/shared/user/user.actions';

const BEARER = 'Bearer ';
const TOKEN = 'access_token';

export const CLIENT_ID = 'crazy-front';

const RESOLVE = {
  AUTH_REQUIRED: {type: 'AUTH_REQUIRED'},
  ACCOUNT_REQUIRED: {type: 'ACCOUNT_REQUIRED', redirectTo: '/accounts', redirectToState: 'accounts' }
};

export const redirectToAuthServer = (AUTHENTICATION_SERVER, path, additionalKey?, additionalValue?) =>
  `${AUTHENTICATION_SERVER}${path}?client_id=${CLIENT_ID}&redirect_uri=${
    window.location.origin
  }/login&response_type=token`
  + (additionalKey ? `&${additionalKey}=${additionalValue}` : '');

export default class AuthService {
  [x: string]: any;

  static $inject = [
    '$localStorage',
    '$http',
    '$q',
    '$window',
    '$location',
    '$state',
    'AccountsService',
    'AUTHENTICATION_SERVER',
    'PermPermissionStore',
    'Users',
    'AmplitudeService',
    'WidgetService',
    'ModalService',
    'LiveChat',
    'Store',
    'LastStateService',
    'ContactsListService'
  ];

  constructor(
    $localStorage,
    $http,
    $q,
    $window,
    $location,
    $state,
    AccountsService,
    AUTHENTICATION_SERVER,
    PermPermissionStore,
    Users,
    AmplitudeService,
    WidgetService,
    ModalService,
    LiveChat,
    Store: Store<IState>,
    LastStateService,
    ContactsService
  ) {
    this.$localStorage = $localStorage;
    this.$http = $http;
    this.$q = $q;
    this.$window = $window;
    this.$location = $location;
    this.$state = $state;
    this.AccountsService = AccountsService;
    this.PermPermissionStore = PermPermissionStore;
    this.Users = Users;
    this.AUTHENTICATION_SERVER = AUTHENTICATION_SERVER;
    this.Amplitude = AmplitudeService;
    this.WidgetService = WidgetService;
    this.ModalService = ModalService;
    this.LiveChat = LiveChat;
    this.store = Store;
    this.LastStateService = LastStateService;
    this.contactsService = ContactsService;
  }

  login() {
    this.Amplitude.logEvent('Panel: Login');
    this.AccountsService.clear();
    this.changeHashBasedLocation();
    this.saveTokenFromUrl();
    this.AccountsService.goToDashboard();
  }

  fetchUser() {
    return this.Users.touchCurrent(
      user => {
        this.user = user;
        this.PermPermissionStore.defineManyPermissions(
          this.user.permissions,
          name => this.user.permissions.indexOf(name) > -1
        );
        this.store.dispatch(new UserAddAction(user));
      },
      () => {
        this.LiveChat.initLiveChatWithoutAuth();
        this.Amplitude.logEvent('Panel: accounts redirect to SSO');
        this.ModalService.showModal(
          {
            title: 'ACCOUNTS.RELOGIN.TITLE',
            body: 'ACCOUNTS.RELOGIN.BODY'
          },
          false,
          'contactSupport.html'
        ).then(() => {
          this.logout();
        });
      }
    ).$promise;
  }

  currentUser() {
    return this.user;
  }

  getUsername() {
    return this.currentUser() ? this.currentUser().username : this.currentUser();
  }

  isChannelsClientType() {
    return this.user.clientType === 'CHANNELS';
  }

  isShoperConnectClientType() {
    return this.user.clientType === 'SHOPER_CONNECT';
  }

  isOwner() {
    return this.user.role.toUpperCase() === 'OWNER';
  }

  addAuthTokenToTheHttpHeaders(authToken = this.$localStorage.authToken) {
    this.$http.defaults.headers.common.Authorization = BEARER + authToken;
  }

  logout() {
    this.Amplitude.logEvent('Panel: Logout');
    delete this.$localStorage.authToken;
    this.$http.defaults.headers.common.Authorization = null;
    this.AccountsService.clear();

    this.contactsService.resetSessionStorage();
    this.contactsService.resetState();
    this.store.dispatch(new LogoutAction());
  }

  assertTokenExists(): Promise<string> {
    const authToken = this.$localStorage.authToken;
    if (!authToken) {
      return this.tryToObtainTokenFromWidget();
    }
    return this.$q.resolve(authToken);
  }

  getToken() {
    return this.$localStorage.authToken;
  }

  checkUser() {
    return this.assertTokenExists().then(() => {
      if (!this.AccountsService.current()) {
        return this.Users.domains().$promise.then((accounts) => {
          this.AccountsService.setAccountsLength(accounts.length);
          if (accounts.length === 1) {
            this.AccountsService.setAccount(accounts[0]);
            return this.fetchUser();
          }
          this.$window.location.href = RESOLVE.ACCOUNT_REQUIRED['redirectTo'];
          return this.$q.reject()
        });
      }
      return this.fetchUser();
    });
  }

  onAuthRequired(href, additionalKey?, additionalValue?, path = '/oauth/authorize') {
    this.LastStateService.setLastState(href);
    // sometimes Firefox doesn't finish saving to the storage if redirect happened
    setTimeout(() => this.redirectOauth(path, additionalKey, additionalValue), 0);
  }

  /***
   * The method is need to populate referrer for analytics.
   * Marketing page links to the app itself and if user is not authorized, he should be redirected to SSO.
   * @param url
   */
  goToUrlAndPreserveReferrer(url) {
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
  }

  redirectOauth(path, additionalKey?, additionalValue?) {
    const url = redirectToAuthServer(this.AUTHENTICATION_SERVER, path, additionalKey, additionalValue);
    this.goToUrlAndPreserveReferrer(url);
  }

  hasPermission(permission: string) {
    return this.PermPermissionStore && this.PermPermissionStore.hasPermissionDefinition(permission);
  }

  private saveAuthToken(token) {
    this.$localStorage.authToken = token;
    this.store.dispatch(new LoginSuccessAction(token));
  }

  private changeHashBasedLocation() {
    this.$location.url(this.$location.url().replace('#', '?')).replace();
  }

  private saveTokenFromUrl() {
    const authToken = String(this.$location.search()[TOKEN]);
    this.saveAuthToken(authToken);
    this.addAuthTokenToTheHttpHeaders(authToken);
  }

  private tryToObtainTokenFromWidget(): Promise<string> {
    return this.WidgetService.getToken().then(token => {
      if (token) {
        this.saveAuthToken(token);
        this.addAuthTokenToTheHttpHeaders(token);
        return this.$q.resolve(token);
      } else {
        return this.missingToken();
      }
    });
  }

  private missingToken() {
    return this.$q.reject(RESOLVE.AUTH_REQUIRED);
  }
}
